/**
 * Loads the Bazaarvoice display loader on PDP (singleton per loader URL).
 */
import {useEffect, useMemo, useState} from 'react';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {
  getBazaarvoiceLoaderUrl,
  isBazaarvoiceConfigured,
} from '@amblique/king-living/app/utils/bazaarvoice';

const bazaarvoiceScriptManager = {
  loadingUrl: null,
  readyUrl: null,
  loadPromise: null,

  /**
   * @param {string} scriptUrl
   * @returns {Promise<void>}
   */
  load(scriptUrl) {
    if (!scriptUrl) {
      return Promise.reject(new Error('Bazaarvoice loader URL is not configured'));
    }

    if (this.readyUrl === scriptUrl) {
      return Promise.resolve();
    }

    if (this.loadingUrl === scriptUrl && this.loadPromise) {
      return this.loadPromise;
    }

    this.loadingUrl = scriptUrl;
    this.readyUrl = null;
    this.loadPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[data-bv-loader="${scriptUrl}"]`);

      if (existingScript) {
        existingScript.addEventListener('load', () => {
          this.readyUrl = scriptUrl;
          this.loadingUrl = null;
          resolve();
        });
        existingScript.addEventListener('error', () => {
          this.loadingUrl = null;
          reject(new Error('Failed to load Bazaarvoice script'));
        });

        return;
      }

      const script = document.createElement('script');

      script.async = true;
      script.src = scriptUrl;
      script.setAttribute('data-bv-loader', scriptUrl);
      script.onload = () => {
        this.readyUrl = scriptUrl;
        this.loadingUrl = null;
        resolve();
      };
      script.onerror = () => {
        this.loadingUrl = null;
        this.loadPromise = null;
        reject(new Error('Failed to load Bazaarvoice script'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  },
};

/**
 * @param {boolean} [enabled=true] - When false, skips loading (e.g. non-PDP routes)
 * @returns {boolean}
 */
export function useBazaarvoiceScript(enabled = true) {
  const [isReady, setIsReady] = useState(false);
  const bazaarvoicePrefs = useSitePreference('bazaarvoice');
  const {locale, site} = useMultiSite();
  const localeId = locale?.id;
  const defaultLocaleId = site?.l10n?.defaultLocale;

  const loaderUrl = useMemo(() => {
    if (!isBazaarvoiceConfigured(bazaarvoicePrefs)) {
      return null;
    }

    return getBazaarvoiceLoaderUrl(bazaarvoicePrefs, localeId, defaultLocaleId);
  }, [bazaarvoicePrefs, localeId, defaultLocaleId]);

  useEffect(() => {
    if (!enabled || !loaderUrl) {
      setIsReady(false);

      return undefined;
    }

    let cancelled = false;

    bazaarvoiceScriptManager.load(loaderUrl)
      .then(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Bazaarvoice script loading error:', error);
          setIsReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, loaderUrl]);

  return isReady;
}
